import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { themeControlDimensions, type ThemeControlSize } from '../../Theme/themePresentation';

export interface CustomSelectOption {
  value: string;
  label: React.ReactNode;
  disabled?: boolean;
}

interface CustomSelectProps {
  value: string;
  options: CustomSelectOption[];
  onChange: (value: string) => void;
  placeholder?: React.ReactNode;
  disabled?: boolean;
  className?: string;
  buttonClassName?: string;
  menuClassName?: string;
  optionClassName?: string;
  align?: 'left' | 'right';
  ariaLabel?: string;
  triggerContent?: React.ReactNode;
  triggerTitle?: string;
  hideCaret?: boolean;
  controlSize?: ThemeControlSize;
}

interface MenuPosition {
  left: number;
  top: number;
  minWidth: number;
  maxHeight: number;
  placement: 'bottom' | 'top';
}

export const CustomSelect: React.FC<CustomSelectProps> = ({
  value,
  options,
  onChange,
  placeholder = 'Select...',
  disabled = false,
  className = '',
  buttonClassName = '',
  menuClassName = '',
  optionClassName = '',
  align = 'left',
  ariaLabel,
  triggerContent,
  triggerTitle,
  hideCaret = false,
  controlSize = 'md',
}) => {
  const listboxId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value]
  );

  const updateMenuPosition = useCallback(() => {
    if (!rootRef.current) return;

    const triggerRect = rootRef.current.getBoundingClientRect();
    const menuWidth = menuRef.current?.offsetWidth || triggerRect.width;
    const viewportPadding = 8;
    const spaceBelow = window.innerHeight - triggerRect.bottom - viewportPadding;
    const spaceAbove = triggerRect.top - viewportPadding;
    const placement = spaceBelow < 120 && spaceAbove > spaceBelow ? 'top' : 'bottom';
    const maxHeight = Math.max(96, Math.min(220, placement === 'bottom' ? spaceBelow : spaceAbove));
    const rawLeft = align === 'right' ? triggerRect.right - menuWidth : triggerRect.left;
    const left = Math.max(
      viewportPadding,
      Math.min(rawLeft, window.innerWidth - menuWidth - viewportPadding)
    );
    const top = placement === 'bottom'
      ? triggerRect.bottom - 1
      : Math.max(viewportPadding, triggerRect.top - maxHeight + 1);

    const nextPosition: MenuPosition = {
      left,
      top,
      minWidth: triggerRect.width,
      maxHeight,
      placement,
    };

    setMenuPosition((current) => {
      if (
        current &&
        Math.abs(current.left - nextPosition.left) < 0.5 &&
        Math.abs(current.top - nextPosition.top) < 0.5 &&
        Math.abs(current.minWidth - nextPosition.minWidth) < 0.5 &&
        Math.abs(current.maxHeight - nextPosition.maxHeight) < 0.5 &&
        current.placement === nextPosition.placement
      ) {
        return current;
      }
      return nextPosition;
    });
  }, [align]);

  useLayoutEffect(() => {
    if (!open) {
      setMenuPosition(null);
      return;
    }

    updateMenuPosition();
  }, [open, updateMenuPosition, options.length]);

  const setMenuNode = useCallback((node: HTMLDivElement | null) => {
    menuRef.current = node;
    if (node) {
      updateMenuPosition();
    }
  }, [updateMenuPosition]);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!rootRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    const handleViewportChange = () => updateMenuPosition();

    document.addEventListener('pointerdown', handlePointerDown, true);
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [open, updateMenuPosition]);

  const selectOption = (option: CustomSelectOption) => {
    if (option.disabled) return;
    onChange(option.value);
    setOpen(false);
  };

  const moveSelection = (direction: 1 | -1) => {
    const enabledOptions = options.filter((option) => !option.disabled);
    if (enabledOptions.length === 0) return;

    const currentIndex = enabledOptions.findIndex((option) => option.value === value);
    const nextIndex = currentIndex === -1
      ? 0
      : (currentIndex + direction + enabledOptions.length) % enabledOptions.length;
    onChange(enabledOptions[nextIndex].value);
  };

  const menu = open && !disabled && menuPosition
    ? createPortal(
      <div
        ref={setMenuNode}
        id={listboxId}
        role="listbox"
        style={{
          left: menuPosition.left,
          top: menuPosition.top,
          minWidth: menuPosition.minWidth,
        }}
        className={`fixed z-9999 bg-[#1a1a2e] border border-white/10 shadow-2xl overflow-hidden backdrop-blur-xl animate-in fade-in slide-in-from-top-1 duration-200 ${menuPosition.placement === 'bottom' ? 'border-t-0 rounded-b-xl' : 'border-b-0 rounded-t-xl'} ${menuClassName}`}
      >
        <div className="py-1 overflow-y-auto ux-scrollbar" style={{ maxHeight: menuPosition.maxHeight }}>
          {options.map((option) => {
            const selected = option.value === value;
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={selected}
                disabled={option.disabled}
                onClick={() => selectOption(option)}
                className={`w-full px-4 py-2 text-left text-[10px] font-bold text-white/70 hover:text-neon hover:bg-white/5 transition-colors border-b border-white/3 last:border-0 flex items-center justify-between h-8 disabled:opacity-40 disabled:cursor-not-allowed ${optionClassName}`}
              >
                <span className="truncate">{option.label}</span>
                {selected && <span className="w-1 h-1 rounded-full bg-neon shadow-[0_0_8px_rgba(var(--neon-rgb),0.5)]" />}
              </button>
            );
          })}
        </div>
      </div>,
      document.body
    )
    : null;

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        aria-label={ariaLabel}
        title={triggerTitle}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            setOpen(false);
            return;
          }
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            if (!open) setOpen(true);
            moveSelection(1);
          }
          if (event.key === 'ArrowUp') {
            event.preventDefault();
            if (!open) setOpen(true);
            moveSelection(-1);
          }
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setOpen((current) => !current);
          }
        }}
        className={`w-full ${themeControlDimensions(controlSize)} bg-white/3 border border-white/10 text-foreground leading-none text-left flex items-center justify-between focus:border-neon/50 focus:bg-white/5 transition disabled:opacity-50 disabled:cursor-not-allowed ${open ? 'rounded-t-lg border-b-0' : 'rounded-lg'} ${buttonClassName}`}
      >
        <span className="truncate">{triggerContent ?? selectedOption?.label ?? placeholder}</span>
        {!hideCaret && (
          <span
            aria-hidden="true"
            className={`ml-2 w-0 h-0 border-l-4 border-l-transparent border-r-4 border-r-transparent border-t-4 transition-transform duration-200 ${open ? 'rotate-180 border-t-neon' : 'border-t-white/40'}`}
          />
        )}
      </button>
      {menu}
    </div>
  );
};
